import { create } from 'zustand';
import { apiFetch } from '@/lib/api-client';
import type { ValidateCouponResponse } from '@/dto/index';

export interface CompanyDetails {
  name: string;
  registrationCode: string;
  vatNumber?: string;
}

export interface CheckoutState {
  email: string;
  phone: string;
  company: CompanyDetails | null;
  needsInvoice: boolean;
  discountCode: string | null;
  discountApplied: boolean;
  discountAmount: number;
  discountPercentage: number;
  originalAmount: number;
  finalAmount: number;
  courseId: string | null;
  courseName: string;
  courseInstructor: string;
  courseDuration: string;
  paymentMethod: string | null;
  loading: {
    discount: boolean;
    payment: boolean;
  };
  error: string | null;
  
  // Actions
  setField: <K extends keyof CheckoutState>(key: K, value: CheckoutState[K]) => void;
  resetCheckout: () => void;
  initializeCheckout: (courseData: any, userData?: { email?: string; phone?: string }) => void;
  toggleInvoice: () => void;
  applyDiscount: (code: string) => Promise<boolean>;
  setPaymentMethod: (method: string) => void;
  processPayment: () => Promise<boolean>;
  recalculateDiscount: () => void;
}

const initialState = {
  email: '',
  phone: '',
  company: null,
  needsInvoice: false,
  discountCode: null,
  discountApplied: false,
  discountAmount: 0,
  discountPercentage: 0,
  originalAmount: 0,
  finalAmount: 0,
  courseId: null,
  courseName: '',
  courseInstructor: '',
  courseDuration: '',
  paymentMethod: null,
  loading: {
    discount: false,
    payment: false,
  },
  error: null,
};

export const useCheckoutStore = create<CheckoutState>()((set, get) => ({
  ...initialState,

  setField: (key, value) => set({ [key]: value }),
  
  resetCheckout: () => set(initialState),
  
  initializeCheckout: (courseData, userData) => {
    if (!courseData) return;
    
    const amount = courseData.price ? Number(courseData.price) * 100 : 0;
    const currentState = get();
    
    // Calculate new finalAmount, taking into account any existing discount
    let finalAmount = amount;
    if (currentState.discountApplied && currentState.discountPercentage > 0) {
      const discountAmount = Math.round(amount * currentState.discountPercentage / 100);
      finalAmount = amount - discountAmount;
    }
    
    set({
      courseId: courseData.id || null,
      courseName: courseData.title || 'Kursus',
      courseInstructor: courseData.instructor || 'Õppejõud',
      courseDuration: courseData.duration || '0h',
      originalAmount: amount,
      finalAmount: finalAmount,
      // Keep existing discount if it was applied
      ...(currentState.discountApplied ? {
        discountAmount: Math.round(amount * currentState.discountPercentage / 100)
      } : {}),
      // Prefill user data if available
      ...(userData?.email && { email: userData.email }),
      ...(userData?.phone && { phone: userData.phone }),
    });
  },
  
  toggleInvoice: () => {
    const needsInvoice = !get().needsInvoice;
    set({ 
      needsInvoice,
      // Clear company data if invoice is no longer needed
      company: needsInvoice ? get().company : null
    });
  },
  
  applyDiscount: async (code) => {
    if (!code) return false;
    
    set({ loading: { ...get().loading, discount: true }, error: null });
    
    try {
      // Call our new API to validate the coupon
      const data = await apiFetch<ValidateCouponResponse>('/api/coupons/validate', {
        method: 'POST',
        body: JSON.stringify({ code })
      });
      
      if ('valid' in data && data.valid) {
        const originalAmount = get().originalAmount;
        const discountPercentage = data.discount_percentage;
        const discountAmount = Math.round(originalAmount * discountPercentage / 100);
        const finalAmount = originalAmount - discountAmount;
        
        set({
          discountCode: data.code,
          discountApplied: true,
          discountAmount,
          discountPercentage,
          finalAmount,
          error: null,
        });
        
        return true;
      } else {
        const errorMessage = 'message' in data ? data.message : ('error' in data ? data.error : 'Kood ei kehti');
        set({ error: errorMessage });
        return false;
      }
    } catch (err) {
      console.error('Error applying discount:', err);
      set({ error: 'Viga koodi rakendamisel' });
      return false;
    } finally {
      set({ loading: { ...get().loading, discount: false } });
    }
  },
  
  recalculateDiscount: () => {
    const { originalAmount, discountApplied, discountPercentage } = get();
    
    if (discountApplied && discountPercentage > 0) {
      const discountAmount = Math.round(originalAmount * discountPercentage / 100);
      const finalAmount = originalAmount - discountAmount;
      
      set({
        discountAmount,
        finalAmount
      });
    }
  },
  
  setPaymentMethod: (method) => {
    set({ paymentMethod: method });
  },
  
  processPayment: async () => {
    set({ loading: { ...get().loading, payment: true }, error: null });
    
    try {
      const {
        email,
        phone,
        company,
        needsInvoice,
        courseId,
        finalAmount,
        paymentMethod
      } = get();
      
      // Record the purchase with additional company info if needed
      const purchaseData = {
        course_id: courseId,
        amount: finalAmount,
        payment_method: paymentMethod,
        email,
        phone,
        ...(needsInvoice && company ? {
          company_name: company.name,
          company_reg_code: company.registrationCode,
          company_vat: company.vatNumber
        } : {})
      };

      // For now, simulate success
      // In real implementation, this would call your Montonio payment handler
      return true;
    } catch (err) {
      console.error('Payment processing error:', err);
      set({ error: 'Makse ebaõnnestus – proovi uuesti.' });
      return false;
    } finally {
      set({ loading: { ...get().loading, payment: false } });
    }
  },
}));
